import { extractArrayValues } from "../pages/csr/utils/saveRestoreUtils";
import { pickSectionPayload } from "./wizardSections";

/**
 * Clean object by checking if all values are null or empty
 */
function cleanObject(obj) {
  for (const key in obj) {
    if (obj[key] === null || obj[key] === "") {
      return true;
    } else if (typeof obj[key] === "object" && cleanObject(obj[key])) {
      return true;
    }
  }
  return false;
}

/**
 * Submission handler shared by every wizard question page.
 *
 * The form controls of all pages live in one `wizardForm`, so the collection /
 * normalisation below still runs over the whole form. What leaves the browser,
 * though, is only the current page's slice: `sections` names the backend
 * section(s) this page owns, and each one is PATCHed with just its own fields.
 * That keeps a save on, say, Safety from overwriting the PK answers.
 */
export const createUnifiedSubmitHandler = ({
  wizardForm,
  qForm,
  studyId,
  sections,
  setAjaxLoading,
  setQuestionsDataSaved,
  questionsDataSaved,
  setFormFilledData,
  setWizardFormFilledData,
  setErrMsg,
  errRef,
  setIsSaving,
  questionsRef,
  CsrAPI,
  toast,
  isProduction,
  Sentry,
}) => {
  return e => {
    e.preventDefault();

    // Set saving flag to prevent form repopulation
    setIsSaving(true);

    // Notify Questions component that save operation is starting
    if (
      questionsRef &&
      questionsRef.current &&
      questionsRef.current.setSavingState
    ) {
      questionsRef.current.setSavingState(true);
    }

    setAjaxLoading(true);

    // Create a copy of the form values to avoid mutating the original form
    let formValuesToSave = JSON.parse(JSON.stringify(wizardForm.value));

    // Merge saved data from questionsDataSaved to include data from all pages
    // This ensures PK/PD data is included even when those controls aren't initialized
    // on the current page (e.g., when on Questions page, PK/PD controls don't exist)
    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const savedFormData = questionsDataSaved.wizardForm;

      // Deep merge: current form values take precedence, but include saved values for missing keys
      const mergeFormData = (current, saved) => {
        const merged = { ...current };

        // Iterate through saved data and add missing keys
        Object.keys(saved).forEach(key => {
          // Skip helper objects
          if (key === "_compUsesPrefixedControls") {
            return;
          }

          // Expand saved block arrays into individual form controls before merging
          // This allows us to collect blocks even when controls aren't initialized on current page
          // Only use saved values if current form doesn't have them (prioritize current form values)
          if (key === "qPkStdAnalysis" && Array.isArray(saved[key])) {
            // If current form already manages Standard PK blocks, trust it (blocks may have been deleted)
            if ("qPkTable" in current) return;
            saved[key].forEach((block, index) => {
              const suffix = index > 0 ? `_${index + 1}` : "";
              if (
                block.qPkTable !== undefined &&
                block.qPkTable !== null &&
                (merged[`qPkTable${suffix}`] === undefined ||
                  merged[`qPkTable${suffix}`] === null ||
                  merged[`qPkTable${suffix}`] === "")
              ) {
                merged[`qPkTable${suffix}`] = block.qPkTable;
              }
              if (
                block.qPkPopulation !== undefined &&
                block.qPkPopulation !== null &&
                (merged[`qPkPopulation${suffix}`] === undefined ||
                  merged[`qPkPopulation${suffix}`] === null ||
                  merged[`qPkPopulation${suffix}`] === "")
              ) {
                merged[`qPkPopulation${suffix}`] = block.qPkPopulation;
              }
              if (
                block.qPkMultiDose !== undefined &&
                block.qPkMultiDose !== null &&
                (merged[`qPkMultiDose${suffix}`] === undefined ||
                  merged[`qPkMultiDose${suffix}`] === null ||
                  merged[`qPkMultiDose${suffix}`] === "")
              ) {
                merged[`qPkMultiDose${suffix}`] = block.qPkMultiDose;
              }
              if (
                block.qPkFigures !== undefined &&
                block.qPkFigures !== null &&
                (!merged[`qPkFigures${suffix}`] ||
                  (Array.isArray(merged[`qPkFigures${suffix}`]) &&
                    merged[`qPkFigures${suffix}`].length === 0))
              ) {
                merged[`qPkFigures${suffix}`] = block.qPkFigures;
              }
              if (
                block.qPkFiguresDescription !== undefined &&
                block.qPkFiguresDescription !== null &&
                (merged[`qPkFiguresDescription${suffix}`] === undefined ||
                  merged[`qPkFiguresDescription${suffix}`] === null ||
                  merged[`qPkFiguresDescription${suffix}`] === "")
              ) {
                merged[`qPkFiguresDescription${suffix}`] =
                  block.qPkFiguresDescription;
              }
              if (
                block.qPkFiguresTimepoints !== undefined &&
                block.qPkFiguresTimepoints !== null &&
                (!merged[`qPkFiguresTimepoints${suffix}`] ||
                  (Array.isArray(merged[`qPkFiguresTimepoints${suffix}`]) &&
                    merged[`qPkFiguresTimepoints${suffix}`].length === 0))
              ) {
                merged[`qPkFiguresTimepoints${suffix}`] =
                  block.qPkFiguresTimepoints;
              }
              if (
                block.qPkFiguresExtra !== undefined &&
                block.qPkFiguresExtra !== null &&
                (!merged[`qPkFiguresExtra${suffix}`] ||
                  (Array.isArray(merged[`qPkFiguresExtra${suffix}`]) &&
                    merged[`qPkFiguresExtra${suffix}`].length === 0))
              ) {
                merged[`qPkFiguresExtra${suffix}`] = block.qPkFiguresExtra;
              }
              if (
                block.qPkTimelines !== undefined &&
                block.qPkTimelines !== null &&
                (!merged[`qPkTimelines${suffix}`] ||
                  (Array.isArray(merged[`qPkTimelines${suffix}`]) &&
                    merged[`qPkTimelines${suffix}`].length === 0))
              ) {
                merged[`qPkTimelines${suffix}`] = block.qPkTimelines;
              }
              if (
                block.qPkStatsAnalysis !== undefined &&
                block.qPkStatsAnalysis !== null &&
                (!merged[`qPkStatsAnalysis${suffix}`] ||
                  (Array.isArray(merged[`qPkStatsAnalysis${suffix}`]) &&
                    merged[`qPkStatsAnalysis${suffix}`].length === 0))
              ) {
                merged[`qPkStatsAnalysis${suffix}`] = block.qPkStatsAnalysis;
              }
              if (
                block.qPkStdColumn !== undefined &&
                block.qPkStdColumn !== null &&
                (!merged[`qPkStdColumn${suffix}`] ||
                  (Array.isArray(merged[`qPkStdColumn${suffix}`]) &&
                    merged[`qPkStdColumn${suffix}`].length === 0))
              ) {
                merged[`qPkStdColumn${suffix}`] = block.qPkStdColumn;
              }
            });
            return;
          }

          if (key === "qPkCompAnalysis" && Array.isArray(saved[key])) {
            // If current form already manages Comparative PK blocks, trust it (blocks may have been deleted)
            if ("qPkTableComp" in current) return;
            saved[key].forEach((block, index) => {
              const suffix = index > 0 ? `_${index + 1}` : "";
              // Always use "Comp" prefix (mirrors addCompPkBlock change)
              const sharedFieldPrefix = "Comp";

              // Try prefixed first, then non-prefixed, only if current form doesn't have it
              const trySetValue = (controlName, value) => {
                if (
                  value !== undefined &&
                  value !== null &&
                  (merged[controlName] === undefined ||
                    merged[controlName] === null ||
                    merged[controlName] === "" ||
                    (Array.isArray(merged[controlName]) &&
                      merged[controlName].length === 0))
                ) {
                  merged[controlName] = value;
                }
              };

              trySetValue(
                `qPkTable${sharedFieldPrefix}${suffix}`,
                block.qPkTable
              );
              trySetValue(
                `qPkPopulation${sharedFieldPrefix}${suffix}`,
                block.qPkPopulation
              );
              trySetValue(
                `qPkMultiDose${sharedFieldPrefix}${suffix}`,
                block.qPkMultiDose
              );
              trySetValue(
                `qPkFigures${sharedFieldPrefix}${suffix}`,
                block.qPkFigures
              );
              trySetValue(
                `qPkFiguresExtra${sharedFieldPrefix}${suffix}`,
                block.qPkFiguresExtra
              );
              trySetValue(
                `qPkTimelines${sharedFieldPrefix}${suffix}`,
                block.qPkTimelines
              );
              trySetValue(
                `qPkStatsAnalysis${sharedFieldPrefix}${suffix}`,
                block.qPkStatsAnalysis
              );
              trySetValue(
                `qPkComparisonName${suffix}`,
                block.qPkComparisonName
              );
              trySetValue(
                `qPkComparisonPairs${suffix}`,
                block.qPkComparisonPairs
              );
              trySetValue(`qPkAnalytes${suffix}`, block.qPkAnalytes);
              trySetValue(`qPkExposure${suffix}`, block.qPkExposure);
              trySetValue(
                `qPkDoseInteraction${suffix}`,
                block.qPkDoseInteraction
              );
            });
            return;
          }

          // Handle qPdStdAnalyses (flat array or legacy object with blocks)
          // Also handle legacy qPdAnalyses array format for backward compatibility
          if (
            (key === "qPdStdAnalyses" &&
              saved[key] &&
              typeof saved[key] === "object") ||
            (key === "qPdAnalyses" && Array.isArray(saved[key]))
          ) {
            // If current form already manages Standard PD blocks, trust it (blocks may have been deleted)
            if ("qPdTable" in current) return;
            const stdData = saved[key];
            const stdBlocks = Array.isArray(stdData)
              ? stdData
              : stdData.blocks || [];
            // Support legacy nested qPdStdEnabled inside qPdStdAnalyses
            if (
              !Array.isArray(stdData) &&
              stdData.qPdStdEnabled !== undefined
            ) {
              merged.qPdStdEnabled = stdData.qPdStdEnabled;
            }
            stdBlocks.forEach((block, index) => {
              const suffix = index > 0 ? `_${index + 1}` : "";
              const trySetValue = (controlName, value) => {
                if (
                  value !== undefined &&
                  value !== null &&
                  (merged[controlName] === undefined ||
                    merged[controlName] === null ||
                    merged[controlName] === "" ||
                    (Array.isArray(merged[controlName]) &&
                      merged[controlName].length === 0))
                ) {
                  merged[controlName] = value;
                }
              };

              trySetValue(`qPdTable${suffix}`, block.qPdTable);
              trySetValue(`qPdPopulation${suffix}`, block.qPdPopulation);
              trySetValue(`qPdComparative${suffix}`, block.qPdComparative);
              trySetValue(`qPdCompName${suffix}`, block.qPdCompName);
              trySetValue(`qPdSummaryType${suffix}`, block.qPdSummaryType);
              trySetValue(`qPdParameters${suffix}`, block.qPdParameters);
              trySetValue(
                `qPdTreatmentGroups${suffix}`,
                block.qPdTreatmentGroups
              );
              trySetValue(
                `qPdComparisonPairs${suffix}`,
                block.qPdComparisonPairs
              );
              trySetValue(
                `qPdDoseInteraction${suffix}`,
                block.qPdDoseInteraction
              );
              trySetValue(`qPdColumnHeaders${suffix}`, block.qPdColumnHeaders);
              trySetValue(`qPdTimepoints${suffix}`, block.qPdTimepoints);
              trySetValue(`qPdStatTables${suffix}`, block.qPdStatTables);
            });
            return;
          }

          if (
            key === "qPdCompAnalyses" &&
            saved[key] &&
            typeof saved[key] === "object"
          ) {
            // If current form already manages Comparative PD blocks, trust it (blocks may have been deleted)
            if ("qPdTableComp" in current) return;
            const compData = saved[key];
            const compBlocks = Array.isArray(compData)
              ? compData
              : compData.blocks || [];
            // Support legacy nested qPdCompEnabled inside qPdCompAnalyses
            if (
              !Array.isArray(compData) &&
              compData.qPdCompEnabled !== undefined
            ) {
              merged.qPdCompEnabled = compData.qPdCompEnabled;
            }
            compBlocks.forEach((block, index) => {
              const suffix = index > 0 ? `_${index + 1}` : "";
              const trySetValue = (controlName, value) => {
                if (
                  value !== undefined &&
                  value !== null &&
                  (merged[controlName] === undefined ||
                    merged[controlName] === null ||
                    merged[controlName] === "" ||
                    (Array.isArray(merged[controlName]) &&
                      merged[controlName].length === 0))
                ) {
                  merged[controlName] = value;
                }
              };

              trySetValue(`qPdTableComp${suffix}`, block.qPdTable);
              trySetValue(`qPdPopulationComp${suffix}`, block.qPdPopulation);
              trySetValue(`qPdComparativeComp${suffix}`, block.qPdComparative);
              trySetValue(`qPdCompNameComp${suffix}`, block.qPdCompName);
              trySetValue(`qPdSummaryTypeComp${suffix}`, block.qPdSummaryType);
              trySetValue(`qPdParametersComp${suffix}`, block.qPdParameters);
              trySetValue(
                `qPdTreatmentGroupsComp${suffix}`,
                block.qPdTreatmentGroups
              );
              trySetValue(
                `qPdComparisonPairsComp${suffix}`,
                block.qPdComparisonPairs
              );
              trySetValue(
                `qPdDoseInteractionComp${suffix}`,
                block.qPdDoseInteraction
              );
              trySetValue(`qPdTimepointsComp${suffix}`, block.qPdTimepoints);
              trySetValue(`qPdStatTablesComp${suffix}`, block.qPdStatTables);
            });
            return;
          }

          if (key === "qPkRelBioAnalyses" && Array.isArray(saved[key])) {
            // If current form already manages Bioavailability blocks, trust it (blocks may have been deleted)
            if ("qPkRelBioTable" in current) return;
            saved[key].forEach((block, index) => {
              const suffix = index > 0 ? `_${index + 1}` : "";
              const trySetValue = (controlName, value) => {
                if (
                  value !== undefined &&
                  value !== null &&
                  (merged[controlName] === undefined ||
                    merged[controlName] === null ||
                    merged[controlName] === "" ||
                    (Array.isArray(merged[controlName]) &&
                      merged[controlName].length === 0))
                ) {
                  merged[controlName] = value;
                }
              };
              trySetValue(`qPkRelBioTable${suffix}`, block.qPkRelBioTable);
              trySetValue(
                `qPkRelBioComparisonPair${suffix}`,
                block.qPkRelBioComparisonPair
              );
              trySetValue(
                `qPkRelBioTimelines${suffix}`,
                block.qPkRelBioTimelines
              );
              trySetValue(
                `qPkRelBioEstimateRange${suffix}`,
                block.qPkRelBioEstimateRange
              );
            });
            return;
          }

          // For other keys, merge normally
          // If key doesn't exist in current form or is empty, add it from saved
          if (
            !(key in merged) ||
            merged[key] === null ||
            merged[key] === undefined ||
            merged[key] === ""
          ) {
            merged[key] = saved[key];
          } else if (
            Array.isArray(saved[key]) &&
            Array.isArray(merged[key]) &&
            merged[key].length === 0
          ) {
            // For arrays, use saved value when current is empty
            merged[key] = saved[key];
          }
        });

        return merged;
      };

      formValuesToSave = mergeFormData(formValuesToSave, savedFormData);
    }

    // Process single array textbox values (e.g. qPrimaryEfficacyTimepointAdditionalNames)
    const formArraysKeysToClean = ["Metabolites"];
    formArraysKeysToClean.forEach(value => {
      if (formValuesToSave[value]) {
        if (value === "Metabolites") {
          if (
            formValuesToSave.qPKmetaboliteNames &&
            formValuesToSave.qPKmetaboliteNames[value]
          ) {
            formValuesToSave.qPKmetaboliteNames[value] =
              formValuesToSave.qPKmetaboliteNames[value].filter(element => {
                return element !== null;
              });
          }
        } else {
          formValuesToSave[value] = formValuesToSave[value].filter(element => {
            return element !== null;
          });
        }
      }
    });

    // General cleanup on form before save
    const wizardFormValue = formValuesToSave;

    // Common form arrays keys that need cleaning
    const formArraysKeys = {
      qEfficacyAnalytes: "object",
      qPrimaryEfficacyComparisonNames: "object",
      qEfficacyPopulations: "object",
      qEfficacyAnalysisTypeNames: "object",
      qEfficacyTimepointNames: "object",
      qEfficacySensitivityNames: "object",
      qEfficacyKaplanMeierAnalytes: "object",
      qTgNamesDoseFinding: "object",
      qTgNames: "object",
      qTgNamesComparison: "object",
      qPrimaryEfficacySensitivityNames: "object",
      qPrimaryEfficacyAnalytes: "object",
      qPrimaryEfficacyPopulation: "object",
      qPrimaryEfficacyAnalysisTypeNames: "object",
      qPrimaryEfficacyAnalytesComparison: "object",
      qPrimaryEfficacyBaseline: "object",
      qPrimaryEfficacyTimepointNames: "object",
      qSecondaryEfficacyAnalytes: "object",
      qSubgroupClassification: "object",
      qPkStdColumn: "object",
      qPkTimelines: "object",
      qPkFiguresTimepoints: "object",
      qPkAnalytes: "object",
    };

    // Clean up common arrays and objects
    Object.keys(wizardFormValue).map(function (key) {
      if (formArraysKeys[key]) {
        if (formArraysKeys[key] === "object") {
          formValuesToSave[key] = wizardFormValue[key].filter(
            obj => !cleanObject(obj)
          );
        } else {
          formValuesToSave[key] = wizardFormValue[key].filter(element => {
            return element !== null;
          });
        }
      }
      return true;
    });

    // Cleanup uncommon structure
    if (
      formValuesToSave.qPKmetaboliteNames &&
      formValuesToSave.qPKmetaboliteNames.Metabolites
    ) {
      formValuesToSave.qPKmetaboliteNames.Metabolites =
        wizardFormValue.qPKmetaboliteNames.Metabolites.filter(element => {
          return element !== null;
        });
    }

    // ============================================
    // COLLECT STANDARD PK ANALYSIS BLOCKS
    // ============================================
    const qPkStdAnalysis = [];
    const isStdEnabled =
      formValuesToSave.qPkStdEnabled === "true" ||
      formValuesToSave.qPkStdEnabled === true;

    if (isStdEnabled) {
      const allBlockNumbers = new Set();

      Object.keys(formValuesToSave).forEach(key => {
        const stdFieldMatch = key.match(
          /^(qPkTable|qPkPopulation|qPkMultiDose|qPkFigures|qPkFiguresDescription|qPkFiguresTimepoints|qPkTimelines|qPkStatsAnalysis|qPkStdColumn|qPkFiguresExtra)(_(\d+))?$/
        );
        if (stdFieldMatch && !key.includes("Comp")) {
          const blockNum = stdFieldMatch[3] ? stdFieldMatch[3] : "1";
          allBlockNumbers.add(blockNum);
        }
      });

      const stdBlockNumbers = Array.from(allBlockNumbers).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      if (stdBlockNumbers.length === 0) {
        stdBlockNumbers.push("1");
      }

      stdBlockNumbers.forEach(blockNum => {
        const blockSuffix = blockNum === "1" ? "" : `_${blockNum}`;

        const qPkTable = formValuesToSave[`qPkTable${blockSuffix}`];
        const qPkPopulation = formValuesToSave[`qPkPopulation${blockSuffix}`];
        const qPkMultiDose = formValuesToSave[`qPkMultiDose${blockSuffix}`];
        const qPkFigures = formValuesToSave[`qPkFigures${blockSuffix}`];
        const qPkFiguresDescription =
          formValuesToSave[`qPkFiguresDescription${blockSuffix}`];
        const qPkFiguresTimepoints =
          formValuesToSave[`qPkFiguresTimepoints${blockSuffix}`];
        const qPkFiguresExtra =
          formValuesToSave[`qPkFiguresExtra${blockSuffix}`];
        const qPkTimelines = formValuesToSave[`qPkTimelines${blockSuffix}`];
        const qPkStatsAnalysis =
          formValuesToSave[`qPkStatsAnalysis${blockSuffix}`];
        const qPkStdColumn =
          formValuesToSave[`qPkStdColumn${blockSuffix}`] ??
          (blockSuffix === "" ? formValuesToSave["qPkStdColumn"] : undefined);

        const blockEntry = {
          qPkTable:
            qPkTable !== undefined && qPkTable !== null ? qPkTable : null,
          qPkPopulation:
            qPkPopulation !== undefined && qPkPopulation !== null
              ? qPkPopulation
              : null,
          qPkMultiDose:
            qPkMultiDose !== undefined && qPkMultiDose !== null
              ? String(qPkMultiDose)
              : "false",
          qPkFigures: Array.isArray(qPkFigures)
            ? extractArrayValues(qPkFigures)
            : qPkFigures !== undefined &&
                qPkFigures !== null &&
                qPkFigures !== ""
              ? [qPkFigures]
              : [],
          qPkFiguresDescription: qPkFiguresDescription ?? null,
          qPkFiguresTimepoints: Array.isArray(qPkFiguresTimepoints)
            ? extractArrayValues(qPkFiguresTimepoints)
            : qPkFiguresTimepoints
              ? [qPkFiguresTimepoints]
              : [],
          qPkFiguresExtra: Array.isArray(qPkFiguresExtra)
            ? extractArrayValues(qPkFiguresExtra)
            : qPkFiguresExtra !== undefined &&
                qPkFiguresExtra !== null &&
                qPkFiguresExtra !== ""
              ? [qPkFiguresExtra]
              : [],
          qPkStdColumn: qPkStdColumn ? extractArrayValues(qPkStdColumn) : [],
          qPkTimelines: Array.isArray(qPkTimelines)
            ? extractArrayValues(qPkTimelines)
            : qPkTimelines !== undefined &&
                qPkTimelines !== null &&
                qPkTimelines !== ""
              ? [qPkTimelines]
              : [],
          qPkStatsAnalysis: Array.isArray(qPkStatsAnalysis)
            ? extractArrayValues(qPkStatsAnalysis)
            : qPkStatsAnalysis !== undefined &&
                qPkStatsAnalysis !== null &&
                qPkStatsAnalysis !== ""
              ? [qPkStatsAnalysis]
              : [],
        };

        const hasData =
          blockEntry.qPkTable !== null ||
          blockEntry.qPkPopulation !== null ||
          blockEntry.qPkMultiDose !== "false" ||
          blockEntry.qPkFigures.length > 0 ||
          blockEntry.qPkStdColumn.length > 0 ||
          blockEntry.qPkTimelines.length > 0 ||
          blockEntry.qPkStatsAnalysis.length > 0;

        if (hasData) {
          // qPKmetaboliteNames belongs in qPkStdAnalysis; store only in first block
          if (blockNum === "1" && formValuesToSave.qPKmetaboliteNames) {
            blockEntry.qPKmetaboliteNames = formValuesToSave.qPKmetaboliteNames;
          }
          qPkStdAnalysis.push(blockEntry);
        }
      });
    }

    // Remove root-level PK attributes after they've been collected
    const pkRootLevelFields = [
      "qPkTable",
      "qPkPopulation",
      "qPkMultiDose",
      "qPkFigures",
      "qPkFiguresDescription",
      "qPkFiguresTimepoints",
      "qPkFiguresExtra",
      "qPkTimelines",
      "qPkStatsAnalysis",
      "qPkStdColumn",
    ];

    pkRootLevelFields.forEach(fieldName => {
      if (Object.prototype.hasOwnProperty.call(formValuesToSave, fieldName)) {
        delete formValuesToSave[fieldName];
      }
      Object.keys(formValuesToSave).forEach(key => {
        if (key.startsWith(fieldName + "_") && !key.includes("Comp")) {
          delete formValuesToSave[key];
        }
      });
    });

    if (qPkStdAnalysis.length > 0) {
      formValuesToSave.qPkStdAnalysis = qPkStdAnalysis;
    }

    // qPKmetaboliteNames must not exist at root; it lives in qPkStdAnalysis (first block)
    delete formValuesToSave.qPKmetaboliteNames;

    // ============================================
    // COLLECT COMPARATIVE PK ANALYSIS BLOCKS
    // ============================================
    const qPkCompAnalysis = [];
    const isCompEnabled =
      formValuesToSave.qPkCompEnabled === "true" ||
      formValuesToSave.qPkCompEnabled === true;

    if (isCompEnabled) {
      const allBlockNumbers = new Set();
      Object.keys(formValuesToSave).forEach(key => {
        const isCompSpecificField =
          /^(qPkComparisonName|qPkComparisonPairs|qPkAnalytes|qPkExposure|qPkDoseInteraction)(_(\d+))?$/.test(
            key
          );
        const isPrefixedField =
          /^(qPkTableComp|qPkPopulationComp|qPkMultiDoseComp|qPkFiguresComp|qPkFiguresExtraComp|qPkTimelinesComp|qPkStatsAnalysisComp)(_(\d+))?$/.test(
            key
          );

        if (isCompSpecificField || isPrefixedField) {
          const suffixMatch = key.match(/_(\d+)$/);
          const blockNum = suffixMatch ? suffixMatch[1] : "1";
          allBlockNumbers.add(blockNum);
        }
      });

      const compBlockNumbers = Array.from(allBlockNumbers).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      if (compBlockNumbers.length === 0) {
        compBlockNumbers.push("1");
      }

      compBlockNumbers.forEach(blockNum => {
        const blockSuffix = blockNum === "1" ? "" : `_${blockNum}`;
        // Always use "Comp" prefix (mirrors addCompPkBlock change)
        const sharedFieldPrefix = "Comp";

        const qPkTable =
          formValuesToSave[`qPkTable${sharedFieldPrefix}${blockSuffix}`];
        const qPkPopulation =
          formValuesToSave[`qPkPopulation${sharedFieldPrefix}${blockSuffix}`];
        const qPkMultiDose =
          formValuesToSave[`qPkMultiDose${sharedFieldPrefix}${blockSuffix}`];
        const qPkFigures =
          formValuesToSave[`qPkFigures${sharedFieldPrefix}${blockSuffix}`];
        const qPkFiguresExtra =
          formValuesToSave[`qPkFiguresExtra${sharedFieldPrefix}${blockSuffix}`];
        const qPkTimelines =
          formValuesToSave[`qPkTimelines${sharedFieldPrefix}${blockSuffix}`];
        const qPkStatsAnalysis =
          formValuesToSave[
            `qPkStatsAnalysis${sharedFieldPrefix}${blockSuffix}`
          ];
        const qPkComparisonName =
          formValuesToSave[`qPkComparisonName${blockSuffix}`];
        const qPkComparisonPairs =
          formValuesToSave[`qPkComparisonPairs${blockSuffix}`];
        const qPkAnalytes = formValuesToSave[`qPkAnalytes${blockSuffix}`];
        const qPkExposure = formValuesToSave[`qPkExposure${blockSuffix}`];
        const qPkDoseInteraction =
          formValuesToSave[`qPkDoseInteraction${blockSuffix}`];

        const blockEntry = {
          qPkTable:
            qPkTable !== undefined && qPkTable !== null ? qPkTable : null,
          qPkPopulation:
            qPkPopulation !== undefined && qPkPopulation !== null
              ? qPkPopulation
              : null,
          qPkMultiDose:
            qPkMultiDose !== undefined && qPkMultiDose !== null
              ? String(qPkMultiDose)
              : "false",
          qPkFigures: Array.isArray(qPkFigures)
            ? extractArrayValues(qPkFigures)
            : qPkFigures !== undefined &&
                qPkFigures !== null &&
                qPkFigures !== ""
              ? [qPkFigures]
              : [],
          qPkFiguresExtra: Array.isArray(qPkFiguresExtra)
            ? extractArrayValues(qPkFiguresExtra)
            : qPkFiguresExtra !== undefined &&
                qPkFiguresExtra !== null &&
                qPkFiguresExtra !== ""
              ? [qPkFiguresExtra]
              : [],
          qPkTimelines: Array.isArray(qPkTimelines)
            ? extractArrayValues(qPkTimelines)
            : qPkTimelines !== undefined &&
                qPkTimelines !== null &&
                qPkTimelines !== ""
              ? [qPkTimelines]
              : [],
          qPkStatsAnalysis: Array.isArray(qPkStatsAnalysis)
            ? extractArrayValues(qPkStatsAnalysis)
            : qPkStatsAnalysis !== undefined &&
                qPkStatsAnalysis !== null &&
                qPkStatsAnalysis !== ""
              ? [qPkStatsAnalysis]
              : [],
          qPkComparisonName:
            qPkComparisonName !== undefined && qPkComparisonName !== null
              ? qPkComparisonName
              : null,
          qPkAnalytes: qPkAnalytes ? extractArrayValues(qPkAnalytes) : [],
          qPkExposure: qPkExposure ? extractArrayValues(qPkExposure) : [],
          qPkDoseInteraction:
            qPkDoseInteraction !== undefined && qPkDoseInteraction !== null
              ? String(qPkDoseInteraction)
              : "false",
          qPkComparisonPairs: (Array.isArray(qPkComparisonPairs)
            ? qPkComparisonPairs
            : qPkComparisonPairs && typeof qPkComparisonPairs === "object"
              ? [qPkComparisonPairs]
              : []
          )
            .map(item => {
              if (typeof item === "object" && item !== null) {
                return {
                  Analyte: item.Analyte || "",
                  Comparator: item.Comparator || "",
                };
              }
              return null;
            })
            .filter(
              item =>
                item !== null && (item.Analyte !== "" || item.Comparator !== "")
            ),
        };

        const hasData =
          blockEntry.qPkTable !== null ||
          blockEntry.qPkPopulation !== null ||
          blockEntry.qPkComparisonName !== null ||
          blockEntry.qPkAnalytes.length > 0 ||
          blockEntry.qPkExposure.length > 0 ||
          blockEntry.qPkComparisonPairs.length > 0;

        if (hasData) {
          qPkCompAnalysis.push(blockEntry);
        }
      });
    }

    // Remove Comparative PK specific fields after collection
    const compPkFields = [
      "qPkComparisonName",
      "qPkComparisonPairs",
      "qPkAnalytes",
      "qPkExposure",
      "qPkDoseInteraction",
    ];

    compPkFields.forEach(fieldName => {
      if (Object.prototype.hasOwnProperty.call(formValuesToSave, fieldName)) {
        delete formValuesToSave[fieldName];
      }
      Object.keys(formValuesToSave).forEach(key => {
        if (key.startsWith(fieldName + "_")) {
          delete formValuesToSave[key];
        }
      });
    });

    // Remove prefixed Comp fields
    Object.keys(formValuesToSave).forEach(key => {
      if (
        key.startsWith("qPkTableComp") ||
        key.startsWith("qPkPopulationComp") ||
        key.startsWith("qPkMultiDoseComp") ||
        key.startsWith("qPkFiguresComp") ||
        key.startsWith("qPkFiguresExtraComp") ||
        key.startsWith("qPkTimelinesComp") ||
        key.startsWith("qPkStatsAnalysisComp")
      ) {
        delete formValuesToSave[key];
      }
    });

    if (qPkCompAnalysis.length > 0) {
      formValuesToSave.qPkCompAnalysis = qPkCompAnalysis;
    }

    // ============================================
    // COLLECT PHARMACODYNAMICS ANALYSIS BLOCKS
    // ============================================
    const qPdStdBlocks = [];
    const isStdPdEnabled =
      formValuesToSave.qPdStdEnabled === "true" ||
      formValuesToSave.qPdStdEnabled === true;
    const isCompPdEnabled =
      formValuesToSave.qPdCompEnabled === "true" ||
      formValuesToSave.qPdCompEnabled === true;

    // Remove deprecated qPd and legacy qPdAnalyses from payload
    delete formValuesToSave.qPd;
    delete formValuesToSave.qPdAnalyses;

    if (isStdPdEnabled) {
      const allBlockNumbers = new Set();

      Object.keys(formValuesToSave).forEach(key => {
        const pdFieldMatch = key.match(
          /^(qPdTable|qPdPopulation|qPdComparative|qPdCompName|qPdSummaryType|qPdParameters|qPdTreatmentGroups|qPdComparisonPairs|qPdDoseInteraction|qPdColumnHeaders|qPdTimepoints|qPdStatTables)(_(\d+))?$/
        );
        if (pdFieldMatch) {
          const blockNum = pdFieldMatch[3] ? pdFieldMatch[3] : "1";
          allBlockNumbers.add(blockNum);
        }
      });

      const pdBlockNumbers = Array.from(allBlockNumbers).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      if (pdBlockNumbers.length === 0) {
        pdBlockNumbers.push("1");
      }

      pdBlockNumbers.forEach(blockNum => {
        const blockSuffix = blockNum === "1" ? "" : `_${blockNum}`;

        const qPdTable = formValuesToSave[`qPdTable${blockSuffix}`];
        const qPdPopulation = formValuesToSave[`qPdPopulation${blockSuffix}`];
        const qPdComparative = formValuesToSave[`qPdComparative${blockSuffix}`];
        const qPdCompName = formValuesToSave[`qPdCompName${blockSuffix}`];
        const qPdSummaryType = formValuesToSave[`qPdSummaryType${blockSuffix}`];
        const qPdParameters =
          formValuesToSave[`qPdParameters${blockSuffix}`] ??
          (blockSuffix === "" ? formValuesToSave["qPdParameters"] : undefined);
        const qPdTreatmentGroups =
          formValuesToSave[`qPdTreatmentGroups${blockSuffix}`] ??
          (blockSuffix === ""
            ? formValuesToSave["qPdTreatmentGroups"]
            : undefined);
        const qPdComparisonPairs =
          formValuesToSave[`qPdComparisonPairs${blockSuffix}`] ??
          (blockSuffix === ""
            ? formValuesToSave["qPdComparisonPairs"]
            : undefined);
        const qPdDoseInteraction =
          formValuesToSave[`qPdDoseInteraction${blockSuffix}`];
        const qPdColumnHeaders =
          formValuesToSave[`qPdColumnHeaders${blockSuffix}`] ??
          (blockSuffix === ""
            ? formValuesToSave["qPdColumnHeaders"]
            : undefined);
        const qPdTimepoints =
          formValuesToSave[`qPdTimepoints${blockSuffix}`] ??
          (blockSuffix === "" ? formValuesToSave["qPdTimepoints"] : undefined);
        const qPdStatTables = formValuesToSave[`qPdStatTables${blockSuffix}`];

        let processedComparisonPairs = [];
        const compPairsArr = Array.isArray(qPdComparisonPairs)
          ? qPdComparisonPairs
          : qPdComparisonPairs && typeof qPdComparisonPairs === "object"
            ? [qPdComparisonPairs]
            : [];
        if (compPairsArr.length > 0) {
          processedComparisonPairs = compPairsArr
            .filter(item => item && (item.Treatment || item.Comparator))
            .map(item => ({
              Comparator: item.Comparator || "",
              Treatment: item.Treatment || "",
            }));
        }

        const blockEntry = {
          qPdBlockNum: parseInt(blockNum, 10),
          qPdTable:
            qPdTable !== undefined && qPdTable !== null ? qPdTable : null,
          qPdPopulation:
            qPdPopulation !== undefined && qPdPopulation !== null
              ? qPdPopulation
              : null,
          qPdComparative:
            qPdComparative !== undefined && qPdComparative !== null
              ? String(qPdComparative)
              : "false",
          qPdCompName:
            qPdCompName !== undefined && qPdCompName !== null
              ? qPdCompName
              : null,
          qPdSummaryType:
            qPdSummaryType !== undefined && qPdSummaryType !== null
              ? String(qPdSummaryType)
              : "false",
          qPdParameters: qPdParameters ? extractArrayValues(qPdParameters) : [],
          qPdTreatmentGroups: qPdTreatmentGroups
            ? extractArrayValues(qPdTreatmentGroups)
            : [],
          qPdComparisonPairs: processedComparisonPairs,
          qPdDoseInteraction:
            qPdDoseInteraction !== undefined && qPdDoseInteraction !== null
              ? String(qPdDoseInteraction)
              : "false",
          qPdColumnHeaders: qPdColumnHeaders
            ? extractArrayValues(qPdColumnHeaders)
            : [],
          qPdTimepoints: qPdTimepoints ? extractArrayValues(qPdTimepoints) : [],
          qPdStatTables: Array.isArray(qPdStatTables)
            ? extractArrayValues(qPdStatTables)
            : qPdStatTables !== undefined &&
                qPdStatTables !== null &&
                qPdStatTables !== ""
              ? [qPdStatTables]
              : [],
        };

        const hasData =
          blockEntry.qPdTable !== null ||
          blockEntry.qPdPopulation !== null ||
          blockEntry.qPdComparative !== "false" ||
          blockEntry.qPdCompName !== null ||
          blockEntry.qPdSummaryType !== "false" ||
          blockEntry.qPdParameters.length > 0 ||
          blockEntry.qPdTreatmentGroups.length > 0 ||
          blockEntry.qPdComparisonPairs.length > 0 ||
          blockEntry.qPdColumnHeaders.length > 0 ||
          blockEntry.qPdTimepoints.length > 0 ||
          blockEntry.qPdStatTables.length > 0;

        if (hasData) {
          qPdStdBlocks.push(blockEntry);
        }
      });

      // Remove PD-specific fields after collection (including block-level fields)
      const pdFields = [
        "qPdTable",
        "qPdPopulation",
        "qPdComparative",
        "qPdCompName",
        "qPdSummaryType",
        "qPdDoseInteraction",
        "qPdStatTables",
        "qPdParameters",
        "qPdTreatmentGroups",
        "qPdComparisonPairs",
        "qPdColumnHeaders",
        "qPdTimepoints",
      ];

      pdFields.forEach(fieldName => {
        if (Object.prototype.hasOwnProperty.call(formValuesToSave, fieldName)) {
          delete formValuesToSave[fieldName];
        }
        Object.keys(formValuesToSave).forEach(key => {
          if (key.startsWith(fieldName + "_")) {
            delete formValuesToSave[key];
          }
        });
      });
    }

    // Set qPdStdEnabled at root level and qPdStdAnalyses as flat array
    // Preserve null when user hasn't made a selection yet (avoid preselecting "No")
    formValuesToSave.qPdStdEnabled = isStdPdEnabled
      ? "true"
      : (formValuesToSave.qPdStdEnabled ?? null);
    formValuesToSave.qPdStdAnalyses = qPdStdBlocks;

    // ============================================
    // COLLECT COMPARATIVE PD ANALYSIS BLOCKS
    // Uses "Comp"-suffixed controls (qPdTableComp*, etc.) to stay isolated
    // from Standard PD's qPdTable* namespace.
    // ============================================
    const qPdCompBlocks = [];

    if (isCompPdEnabled) {
      const compPdBlockNumbers = new Set();

      Object.keys(formValuesToSave).forEach(key => {
        const compPdFieldMatch = key.match(
          /^(qPdTableComp|qPdPopulationComp|qPdComparativeComp|qPdCompNameComp|qPdSummaryTypeComp|qPdParametersComp|qPdTreatmentGroupsComp|qPdComparisonPairsComp|qPdDoseInteractionComp|qPdTimepointsComp|qPdStatTablesComp)(_(\d+))?$/
        );
        if (compPdFieldMatch) {
          const blockNum = compPdFieldMatch[3] ? compPdFieldMatch[3] : "1";
          compPdBlockNumbers.add(blockNum);
        }
      });

      const compPdBlocks = Array.from(compPdBlockNumbers).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      compPdBlocks.forEach(blockNum => {
        const blockSuffix = blockNum === "1" ? "" : `_${blockNum}`;

        const qPdTable = formValuesToSave[`qPdTableComp${blockSuffix}`];
        const qPdPopulation =
          formValuesToSave[`qPdPopulationComp${blockSuffix}`];
        const qPdComparative =
          formValuesToSave[`qPdComparativeComp${blockSuffix}`];
        const qPdCompName = formValuesToSave[`qPdCompNameComp${blockSuffix}`];
        const qPdSummaryType =
          formValuesToSave[`qPdSummaryTypeComp${blockSuffix}`];
        const qPdParameters =
          formValuesToSave[`qPdParametersComp${blockSuffix}`];
        const qPdTreatmentGroups =
          formValuesToSave[`qPdTreatmentGroupsComp${blockSuffix}`];
        const qPdComparisonPairs =
          formValuesToSave[`qPdComparisonPairsComp${blockSuffix}`];
        const qPdDoseInteraction =
          formValuesToSave[`qPdDoseInteractionComp${blockSuffix}`];
        const qPdTimepoints =
          formValuesToSave[`qPdTimepointsComp${blockSuffix}`];
        const qPdStatTables =
          formValuesToSave[`qPdStatTablesComp${blockSuffix}`];

        let processedComparisonPairs = [];
        const compPdPairsArr = Array.isArray(qPdComparisonPairs)
          ? qPdComparisonPairs
          : qPdComparisonPairs && typeof qPdComparisonPairs === "object"
            ? [qPdComparisonPairs]
            : [];
        if (compPdPairsArr.length > 0) {
          processedComparisonPairs = compPdPairsArr
            .filter(item => item && (item.Treatment || item.Comparator))
            .map(item => ({
              Comparator: item.Comparator || "",
              Treatment: item.Treatment || "",
            }));
        }

        const blockEntry = {
          qPdBlockNum: parseInt(blockNum, 10),
          qPdTable:
            qPdTable !== undefined && qPdTable !== null ? qPdTable : null,
          qPdPopulation:
            qPdPopulation !== undefined && qPdPopulation !== null
              ? qPdPopulation
              : null,
          qPdComparative:
            qPdComparative !== undefined && qPdComparative !== null
              ? String(qPdComparative)
              : "false",
          qPdCompName:
            qPdCompName !== undefined && qPdCompName !== null
              ? qPdCompName
              : null,
          qPdSummaryType:
            qPdSummaryType !== undefined && qPdSummaryType !== null
              ? String(qPdSummaryType)
              : "false",
          qPdParameters: qPdParameters ? extractArrayValues(qPdParameters) : [],
          qPdTreatmentGroups: qPdTreatmentGroups
            ? extractArrayValues(qPdTreatmentGroups)
            : [],
          qPdComparisonPairs: processedComparisonPairs,
          qPdDoseInteraction:
            qPdDoseInteraction !== undefined && qPdDoseInteraction !== null
              ? String(qPdDoseInteraction)
              : "false",
          qPdTimepoints: qPdTimepoints ? extractArrayValues(qPdTimepoints) : [],
          qPdStatTables: Array.isArray(qPdStatTables)
            ? extractArrayValues(qPdStatTables)
            : qPdStatTables !== undefined &&
                qPdStatTables !== null &&
                qPdStatTables !== ""
              ? [qPdStatTables]
              : [],
        };

        const hasData =
          (blockEntry.qPdTable !== null && blockEntry.qPdTable !== "") ||
          (blockEntry.qPdPopulation !== null &&
            blockEntry.qPdPopulation !== "") ||
          blockEntry.qPdComparative !== "false" ||
          (blockEntry.qPdCompName !== null && blockEntry.qPdCompName !== "") ||
          blockEntry.qPdParameters.length > 0 ||
          blockEntry.qPdTreatmentGroups.length > 0 ||
          blockEntry.qPdComparisonPairs.length > 0 ||
          blockEntry.qPdTimepoints.length > 0 ||
          blockEntry.qPdStatTables.length > 0;

        if (hasData) {
          qPdCompBlocks.push(blockEntry);
        }
      });

      // Remove Comp PD fields from formValuesToSave after collection
      const compPdFields = [
        "qPdTableComp",
        "qPdPopulationComp",
        "qPdComparativeComp",
        "qPdCompNameComp",
        "qPdSummaryTypeComp",
        "qPdParametersComp",
        "qPdTreatmentGroupsComp",
        "qPdComparisonPairsComp",
        "qPdDoseInteractionComp",
        "qPdTimepointsComp",
        "qPdStatTablesComp",
      ];

      compPdFields.forEach(fieldName => {
        if (Object.prototype.hasOwnProperty.call(formValuesToSave, fieldName)) {
          delete formValuesToSave[fieldName];
        }
        Object.keys(formValuesToSave).forEach(key => {
          if (key.startsWith(fieldName + "_")) {
            delete formValuesToSave[key];
          }
        });
      });
    }

    // Set qPdCompEnabled at root level and qPdCompAnalyses as flat array
    // Preserve null when user hasn't made a selection yet (avoid preselecting "No")
    formValuesToSave.qPdCompEnabled = isCompPdEnabled
      ? "true"
      : (formValuesToSave.qPdCompEnabled ?? null);
    formValuesToSave.qPdCompAnalyses = qPdCompBlocks;

    // ============================================
    // COLLECT RELATIVE BIOAVAILABILITY BLOCKS
    // ============================================
    const qPkRelBioAnalyses = [];
    const isRelBioEnabled =
      formValuesToSave.qPkRelBio === "true" ||
      formValuesToSave.qPkRelBio === true;

    if (isRelBioEnabled) {
      const relBioBlockNumbers = new Set();
      Object.keys(formValuesToSave).forEach(key => {
        const match = key.match(
          /^(qPkRelBioTable|qPkRelBioComparisonPair|qPkRelBioTimelines|qPkRelBioEstimateRange)(_(\d+))?$/
        );
        if (match) {
          const blockNum = match[3] ? match[3] : "1";
          relBioBlockNumbers.add(blockNum);
        }
      });

      const relBioBlocks = Array.from(relBioBlockNumbers).sort(
        (a, b) => parseInt(a) - parseInt(b)
      );
      if (relBioBlocks.length === 0) relBioBlocks.push("1");

      relBioBlocks.forEach(blockNum => {
        const blockSuffix = blockNum === "1" ? "" : `_${blockNum}`;

        const qPkRelBioTable = formValuesToSave[`qPkRelBioTable${blockSuffix}`];
        const qPkRelBioComparisonPair =
          formValuesToSave[`qPkRelBioComparisonPair${blockSuffix}`];
        const qPkRelBioTimelines =
          formValuesToSave[`qPkRelBioTimelines${blockSuffix}`];
        const qPkRelBioEstimateRange =
          formValuesToSave[`qPkRelBioEstimateRange${blockSuffix}`];

        let comparisonPair = { Test: "", Reference: "" };
        if (qPkRelBioComparisonPair) {
          if (
            Array.isArray(qPkRelBioComparisonPair) &&
            qPkRelBioComparisonPair.length > 0
          ) {
            const first = qPkRelBioComparisonPair[0];
            comparisonPair = {
              Test: first?.Test ?? "",
              Reference: first?.Reference ?? "",
            };
          } else if (
            typeof qPkRelBioComparisonPair === "object" &&
            !Array.isArray(qPkRelBioComparisonPair)
          ) {
            comparisonPair = {
              Test: qPkRelBioComparisonPair.Test ?? "",
              Reference: qPkRelBioComparisonPair.Reference ?? "",
            };
          }
        }

        let estimateRange = { min: null, max: null };
        if (qPkRelBioEstimateRange) {
          const source =
            Array.isArray(qPkRelBioEstimateRange) &&
            qPkRelBioEstimateRange.length > 0
              ? qPkRelBioEstimateRange[0]
              : typeof qPkRelBioEstimateRange === "object" &&
                  !Array.isArray(qPkRelBioEstimateRange)
                ? qPkRelBioEstimateRange
                : null;
          if (source) {
            const minVal = source.min;
            const maxVal = source.max;
            const minNum =
              minVal !== "" && minVal !== null && minVal !== undefined
                ? parseFloat(minVal)
                : null;
            const maxNum =
              maxVal !== "" && maxVal !== null && maxVal !== undefined
                ? parseFloat(maxVal)
                : null;
            estimateRange = {
              min: minNum !== null && !Number.isNaN(minNum) ? minNum : null,
              max: maxNum !== null && !Number.isNaN(maxNum) ? maxNum : null,
            };
          }
        }

        const timelines = qPkRelBioTimelines
          ? extractArrayValues(qPkRelBioTimelines)
          : [];

        const qPkRelBioTableValue =
          qPkRelBioTable !== undefined && qPkRelBioTable !== null
            ? Array.isArray(qPkRelBioTable)
              ? (qPkRelBioTable[0] ?? "")
              : qPkRelBioTable
            : "";

        qPkRelBioAnalyses.push({
          qPkRelBioTable:
            typeof qPkRelBioTableValue === "string"
              ? qPkRelBioTableValue
              : String(qPkRelBioTableValue || ""),
          qPkRelBioComparisonPair: comparisonPair,
          qPkRelBioTimelines: timelines.filter(s => s !== "" && s != null),
          qPkRelBioEstimateRange: estimateRange,
        });
      });

      formValuesToSave.qPkRelBio = "true";
      formValuesToSave.qPkRelBioAnalyses = qPkRelBioAnalyses;

      // Remove root-level RelBio fields (they live inside qPkRelBioAnalyses)
      const relBioRootFields = [
        "qPkRelBioTable",
        "qPkRelBioComparisonPair",
        "qPkRelBioTimelines",
        "qPkRelBioEstimateRange",
      ];
      relBioRootFields.forEach(fieldName => {
        delete formValuesToSave[fieldName];
        Object.keys(formValuesToSave).forEach(key => {
          if (key.startsWith(fieldName + "_")) {
            delete formValuesToSave[key];
          }
        });
      });
    } else {
      // Preserve null when user hasn't made a selection yet (avoid preselecting "No")
      formValuesToSave.qPkRelBio = formValuesToSave.qPkRelBio ?? null;
      formValuesToSave.qPkRelBioAnalyses = [];
    }

    // ============================================
    // AUTO-SET qPK IF ANY PK SUB-SECTION IS ENABLED
    // ============================================
    if (
      formValuesToSave.qPkStdEnabled === "true" ||
      formValuesToSave.qPkCompEnabled === "true" ||
      formValuesToSave.qPkRelBio === "true"
    ) {
      formValuesToSave.qPK = "true";
    }

    // ============================================
    // SUBMIT THIS PAGE'S SECTION(S) TO THE API
    // ============================================
    const sectionPayloads = (sections || []).map(section => ({
      section,
      sectionData: pickSectionPayload(section, formValuesToSave),
    }));

    // Only the fields this page owns; everything else stays untouched server-side.
    const savedFields = Object.assign(
      {},
      ...sectionPayloads.map(({ sectionData }) => sectionData)
    );

    setFormFilledData(qForm.value);
    setWizardFormFilledData(formValuesToSave);

    // Return the Promise so it can be awaited by navigation handlers
    return Promise.all(
      sectionPayloads.map(({ section, sectionData }) =>
        CsrAPI.saveWizardSection({ studyId, section, sectionData })
      )
    )
      .then(() => {
        setErrMsg("");
        toast.success("Data saved successfully!");

        // Merge what we just saved back into the cached section data so the
        // form is not reset. Sections owned by other pages are left alone.
        const nextWizardForm = {
          ...(questionsDataSaved?.wizardForm || {}),
          ...savedFields,
        };
        // Only update if the data is different to avoid unnecessary re-renders
        if (
          JSON.stringify(questionsDataSaved?.wizardForm) !==
          JSON.stringify(nextWizardForm)
        ) {
          setQuestionsDataSaved({
            ...questionsDataSaved,
            wizardForm: nextWizardForm,
          });
        }

        // Also update the form data to ensure consistency
        setFormFilledData(qForm.value);
        setWizardFormFilledData(formValuesToSave);

        return Promise.resolve(); // Explicitly return resolved promise for success
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to save questions");
        }
        errRef.current?.focus();
        return Promise.reject(error); // Re-throw the error for navigation handler
      })
      .finally(() => {
        setAjaxLoading(false);

        // Reset saving flag
        setIsSaving(false);

        // Notify Questions component that save operation is complete
        if (
          questionsRef &&
          questionsRef.current &&
          questionsRef.current.setSavingState
        ) {
          questionsRef.current.setSavingState(false);
        }
      });
  };
};
